import { API, deleteRequest, getRequest, patchRequest, postRequest } from '@/api/api';

import type {
  AwayLeaveDto,
  CreateLeaveDto,
  DeclineLeaveDto,
  LeaveDto,
  LeaveEventConflictDto,
  LeavesFilterDto,
  UpdateLeaveDto,
} from './leaves.dto';
import { mapAwayLeave, mapLeave, mapLeaveConflict } from './leaves.mappers';

/**
 * The active vessel's leaves (`X-Service-Id`). Crew get only their own whatever the filter
 * says; the owner group and captains get everyone's.
 */
export const getLeaves = async (filter: LeavesFilterDto = {}) => {
  const { data } = await getRequest<LeaveDto[]>(API.PEOPLE, '/leaves', false, filter);
  return (data ?? []).map(mapLeave);
};

/** Who is on approved leave on any of these days, for assigning people to an event. */
export const getAwayLeaves = async (params: { from: string; to: string }) => {
  const { data } = await getRequest<AwayLeaveDto[]>(API.PEOPLE, '/leaves/away', false, params);
  return (data ?? []).map(mapAwayLeave);
};

/** The person's events over these days, for the form to warn about before it is sent. */
export const getLeaveConflicts = async (params: {
  person_id?: string;
  start_date: string;
  end_date?: string;
}) => {
  const { data } = await getRequest<LeaveEventConflictDto[]>(
    API.PEOPLE,
    '/leaves/conflicts',
    false,
    params,
  );
  return (data ?? []).map(mapLeaveConflict);
};

export const createLeave = async (body: CreateLeaveDto) => {
  const { data } = await postRequest<LeaveDto, CreateLeaveDto>(API.PEOPLE, '/leaves', body);
  return mapLeave(data);
};

export const updateLeave = async (id: string, body: UpdateLeaveDto) => {
  const { data } = await patchRequest<LeaveDto, UpdateLeaveDto>(API.PEOPLE, `/leaves/${id}`, body);
  return mapLeave(data);
};

export const declineLeave = async (id: string, body: DeclineLeaveDto) => {
  const { data } = await postRequest<LeaveDto, DeclineLeaveDto>(
    API.PEOPLE,
    `/leaves/${id}/decline`,
    body,
  );
  return mapLeave(data);
};

/** Erases the leave from the record; whoever it showed on leave is back on board. */
export const deleteLeave = async (id: string) => {
  await deleteRequest(API.PEOPLE, `/leaves/${id}`);
};

/** Approve, cancel and "back from leave" take nothing but the leave. */
export const leaveTransition = async (id: string, action: 'approve' | 'cancel' | 'close') => {
  const { data } = await postRequest<LeaveDto, Record<string, never>>(
    API.PEOPLE,
    `/leaves/${id}/${action}`,
    {},
  );
  return mapLeave(data);
};
